import React, { useEffect, useState } from 'react';
import API_CONFIG from '../config/api';
import { CalendarClock, ChevronDown, History, MapPin, MessageSquare, Phone, X, Users } from 'lucide-react';

interface FicheClient {
    client: {
        id: string;
        squareId: string | null;
        name: string;
        phoneNumber: string;
        address: string;
        city: string;
        district: string;
        sector: string;
        bookingCount: number;
        isFrequentClient: boolean;
        notes: string | string[] | null;
        createdAt: string | null;
    };
    typePiscine: { valeur: string; source: string } | null;
    prochainsSquare: { debut: string; service: string | null; statut: string }[];
    historiqueSquare: { date: string; service: string }[];
    rdvAVenir: RdvTableau[];
    rdvPasses: RdvTableau[];
}

interface RdvTableau {
    id: string;
    date: string;
    heure: string;
    service: string;
    statut: string;
    bookeDansSquare: boolean;
    notes: string | string[] | null;
}

// Une ligne de la liste des rendez-vous, peu importe d'où il vient
interface LigneRdv {
    cle: string;
    date: string; // YYYY-MM-DD
    heure: string;
    service: string;
    origine: 'Square' | 'Tableau de bord';
}

const TZ = 'America/Toronto';

// « 30 sept. 2026 » ; l'année est omise pour l'année en cours
const dateCourte = (date: string) => {
    const d = new Date(`${date}T12:00:00`);
    return d.toLocaleDateString('fr-CA', {
        day: 'numeric',
        month: 'short',
        ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' as const } : {}),
    });
};

const joursAvant = (date: string) => {
    const aujourdhui = new Intl.DateTimeFormat('fr-CA', { timeZone: TZ }).format(new Date());
    const ecart = Math.round(
        (new Date(`${date}T12:00:00`).getTime() - new Date(`${aujourdhui}T12:00:00`).getTime()) / 86400000
    );
    if (ecart === 0) return "aujourd'hui";
    if (ecart === 1) return 'demain';
    if (ecart > 0) return `dans ${ecart} jours`;
    if (ecart === -1) return 'hier';
    return `il y a ${-ecart} jours`;
};

/** Fusionne Square et tableau de bord ; un même jour n'apparaît qu'une fois (Square prime) */
function fusionner(square: LigneRdv[], tableau: LigneRdv[]): LigneRdv[] {
    const joursSquare = new Set(square.map((r) => r.date));
    return [...square, ...tableau.filter((r) => !joursSquare.has(r.date))];
}

const ClientFiche = ({
    clientId,
    onClose,
    onVoirProximite,
}: {
    clientId: string;
    onClose: () => void;
    onVoirProximite: (address: string) => void;
}) => {
    const [fiche, setFiche] = useState<FicheClient | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let annule = false;
        setLoading(true);
        setError('');
        setFiche(null);
        fetch(`${API_CONFIG.endpoints.clients}/${clientId}/fiche`)
            .then((r) => r.json())
            .then((data) => {
                if (annule) return;
                if (!data.success) throw new Error(data.error || 'Fiche introuvable');
                setFiche(data.data);
            })
            .catch((err) => !annule && setError(err instanceof Error ? err.message : 'Erreur de chargement'))
            .finally(() => !annule && setLoading(false));
        return () => {
            annule = true;
        };
    }, [clientId]);

    const carte = 'rounded-xl border border-indigo-500/20 bg-gray-900/70 px-4 py-3 text-sm';

    if (loading) {
        return (
            <div className={`${carte} flex items-center gap-3 text-gray-300`}>
                <div className="animate-spin h-5 w-5 border-2 border-cyan-400 border-t-transparent rounded-full" />
                Chargement de la fiche…
            </div>
        );
    }
    if (error || !fiche) {
        return (
            <div className="text-rose-300 p-3 bg-rose-900/40 rounded-lg border border-rose-500/50">
                {error || 'Fiche introuvable'}
            </div>
        );
    }

    const { client } = fiche;

    const aVenir = fusionner(
        fiche.prochainsSquare.map((b) => {
            const d = new Date(b.debut);
            const heure = d.toLocaleTimeString('fr-CA', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
            return {
                cle: `sq-${b.debut}`,
                date: new Intl.DateTimeFormat('fr-CA', { timeZone: TZ }).format(d),
                // Réservation à minuit = journée entière, pas d'heure à afficher
                heure: /^00\D+00/.test(heure) ? '' : heure,
                service: b.service || 'Rendez-vous',
                origine: 'Square' as const,
            };
        }),
        fiche.rdvAVenir.map((r) => ({
            cle: `tb-${r.id}`,
            date: r.date,
            heure: r.heure,
            service: r.service || 'Rendez-vous',
            origine: 'Tableau de bord' as const,
        }))
    ).sort((a, b) => (a.date + a.heure).localeCompare(b.date + b.heure));

    const passes = fusionner(
        fiche.historiqueSquare.map((s, i) => ({
            cle: `hs-${s.date}-${i}`,
            date: s.date,
            heure: '',
            service: s.service,
            origine: 'Square' as const,
        })),
        fiche.rdvPasses.map((r) => ({
            cle: `tp-${r.id}`,
            date: r.date,
            heure: r.heure,
            service: r.service || 'Rendez-vous',
            origine: 'Tableau de bord' as const,
        }))
    ).sort((a, b) => b.date.localeCompare(a.date));

    const dernier = passes[0];
    const prochain = aVenir[0];

    const pastille = (texte: string, couleur: string) => (
        <span className={`px-2 py-0.5 rounded-full text-[11px] leading-4 border ${couleur}`}>{texte}</span>
    );

    const action =
        'flex items-center justify-center gap-1.5 h-9 rounded-lg text-xs font-medium border transition-colors';

    return (
        <div className="rounded-xl border border-indigo-500/20 bg-gray-900/70 overflow-hidden">
            {/* En-tête */}
            <div className="px-4 pt-3.5 pb-3">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-white leading-tight break-words">
                            {client.name || 'Sans nom'}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="-mr-1.5 -mt-0.5 p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/5"
                        aria-label="Fermer la fiche"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {client.isFrequentClient && (
                    <div className="mt-1.5">
                        {pastille('Client fréquent', 'text-amber-300 border-amber-500/30 bg-amber-500/10')}
                    </div>
                )}

                <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                    <dt className="text-gray-500">Adresse</dt>
                    <dd className="text-gray-200 break-words">
                        {client.address ? (
                            <a
                                href={`https://maps.apple.com/?q=${encodeURIComponent(client.address)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline decoration-gray-500 underline-offset-2 hover:text-cyan-300 hover:decoration-cyan-300"
                            >
                                {client.address}
                            </a>
                        ) : '—'}
                    </dd>
                    <dt className="text-gray-500">Téléphone</dt>
                    <dd className="text-gray-200">{client.phoneNumber || '—'}</dd>
                    <dt className="text-gray-500">Secteur</dt>
                    <dd className="text-gray-200">
                        {[client.sector, client.district && client.district !== client.sector ? client.district : '']
                            .filter(Boolean)
                            .join(' · ') || '—'}
                    </dd>
                    <dt className="text-gray-500">Piscine</dt>
                    <dd className="text-gray-200">{fiche.typePiscine ? fiche.typePiscine.valeur : 'Inconnue'}</dd>
                </dl>

                <div className="grid grid-cols-2 gap-2 mt-3">
                    {client.phoneNumber ? (
                        <a href={`tel:${client.phoneNumber}`} className={`${action} text-cyan-200 border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20`}>
                            <Phone className="h-3.5 w-3.5" /> Appeler
                        </a>
                    ) : <span />}
                    {client.phoneNumber ? (
                        <a href={`sms:${client.phoneNumber}`} className={`${action} text-emerald-200 border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20`}>
                            <MessageSquare className="h-3.5 w-3.5" /> Texter
                        </a>
                    ) : <span />}
                    {client.address ? (
                        <a
                            href={`https://waze.com/ul?q=${encodeURIComponent(client.address)}&navigate=yes`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`${action} text-indigo-200 border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20`}
                        >
                            <MapPin className="h-3.5 w-3.5" /> Itinéraire
                        </a>
                    ) : <span />}
                    {client.address ? (
                        <button
                            onClick={() => onVoirProximite(client.address)}
                            className={`${action} text-gray-300 border-white/10 bg-white/5 hover:bg-white/10`}
                        >
                            <Users className="h-3.5 w-3.5" /> Voisins
                        </button>
                    ) : <span />}
                </div>
            </div>

            {/* Prochain rendez-vous : l'info qu'on vient chercher */}
            <div className="px-4 py-3 border-t border-white/5 bg-cyan-500/[0.06]">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-cyan-400/80">
                    <CalendarClock className="h-3.5 w-3.5" /> Prochain rendez-vous
                </div>
                {prochain ? (
                    <div className="mt-1 flex items-baseline justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-white">{prochain.service}</div>
                            <div className="text-xs text-gray-400">
                                {dateCourte(prochain.date)}
                                {prochain.heure ? ` · ${prochain.heure}` : ''}
                            </div>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-cyan-300">{joursAvant(prochain.date)}</span>
                    </div>
                ) : (
                    <div className="mt-1 text-sm text-gray-400">Aucun rendez-vous prévu</div>
                )}
                {dernier && (
                    <div className="mt-2 text-xs text-gray-500">
                        Dernier service : {dernier.service.toLowerCase()}, {joursAvant(dernier.date)}
                    </div>
                )}
            </div>

            {aVenir.length > 1 && (
                <Section titre="Rendez-vous à venir" icone={<CalendarClock className="h-4 w-4 text-cyan-300" />} compte={aVenir.length}>
                    <ListeRdv lignes={aVenir} avecDelai />
                </Section>
            )}

            <Section titre="Historique" icone={<History className="h-4 w-4 text-indigo-300" />} compte={passes.length}>
                {passes.length === 0 ? (
                    <p className="text-sm text-gray-500">Aucun service depuis janvier 2024.</p>
                ) : (
                    <ListeRdv lignes={passes} />
                )}
            </Section>

        </div>
    );
};

/** Bloc repliable : fermé par défaut, sauf `ouvert` */
const Section = ({
    titre,
    icone,
    compte,
    ouvert = false,
    children,
}: {
    titre: string;
    icone: React.ReactNode;
    compte?: number;
    ouvert?: boolean;
    children: React.ReactNode;
}) => {
    const [deplie, setDeplie] = useState(ouvert);
    return (
        <div className="border-t border-white/5">
            <button
                onClick={() => setDeplie(!deplie)}
                className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-white/[0.03]"
                aria-expanded={deplie}
            >
                {icone}
                <span className="text-sm font-medium text-gray-200">{titre}</span>
                {compte !== undefined && <span className="text-xs text-gray-500">{compte}</span>}
                <ChevronDown className={`ml-auto h-4 w-4 text-gray-500 transition-transform ${deplie ? 'rotate-180' : ''}`} />
            </button>
            {deplie && <div className="px-4 pb-4">{children}</div>}
        </div>
    );
};

const ListeRdv = ({ lignes, avecDelai = false }: { lignes: LigneRdv[]; avecDelai?: boolean }) => (
    <ul className="divide-y divide-white/5 rounded-lg border border-white/5 bg-white/[0.03]">
        {lignes.map((r) => (
            <li key={r.cle} className="px-3 py-2.5 flex items-baseline justify-between gap-3">
                <div className="min-w-0 text-sm text-gray-200">
                    {r.service}
                    {r.origine === 'Tableau de bord' && <span className="ml-1.5 text-[10px] text-gray-500">bot</span>}
                </div>
                <div className="shrink-0 text-right text-xs text-gray-400">
                    {dateCourte(r.date)}
                    {r.heure ? ` · ${r.heure}` : ''}
                    {avecDelai && <div className="text-cyan-300">{joursAvant(r.date)}</div>}
                </div>
            </li>
        ))}
    </ul>
);

export default ClientFiche;
